import type {
  ServiceStockClips,
  StockClipSummary,
} from '@borradh-workspace/api-client/types';
import {
  bodyRegionLabels,
  serviceStockClipModeLabels,
} from '@borradh-workspace/api-client/types';
import type { TreatmentCatalogueEntry } from '@borradh-workspace/labels';
import { Film, Search } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';

import { useOrganizationServiceStockClips } from '../api';
import { ChooseServiceClipsDialog } from './stock-footage/choose-service-clips-dialog';
import { ServiceTagsDialog } from './stock-footage/service-tags-dialog';

/** Past this many services, a search box helps find one. */
const SEARCH_FROM = 8;
/** Thumbnails shown per service before "+N". */
const THUMBNAILS = 4;

interface OrganizationStockFootageProps {
  organizationId: string;
}

/**
 * The stock clips each of a clinic's services uses in its videos. A service
 * uses the automatic match for its treatment until an admin chooses clips,
 * which then replace it.
 */
export function OrganizationStockFootage({
  organizationId,
}: OrganizationStockFootageProps) {
  const { stockFootage, isLoading, isError, error } =
    useOrganizationServiceStockClips(organizationId);
  const [search, setSearch] = useState('');
  const [choosingId, setChoosingId] = useState<string | null>(null);
  const [taggingId, setTaggingId] = useState<string | null>(null);

  const services = stockFootage?.services ?? [];
  const techniques = stockFootage?.techniques ?? [];
  const clipsById = useMemo(
    () => new Map((stockFootage?.clips ?? []).map((c) => [c.id, c])),
    [stockFootage]
  );
  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q
      ? services.filter((s) => s.name.toLowerCase().includes(q))
      : services;
  }, [services, search]);
  const choosing = services.find((s) => s.id === choosingId) ?? null;
  const tagging = services.find((s) => s.id === taggingId) ?? null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Film className="size-5" />
          Stock footage
        </CardTitle>
        <CardDescription>
          The stock clips each service's videos use. Choose clips to replace the
          automatic match.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-14 w-full" />
          </div>
        ) : isError ? (
          <p className="text-destructive text-sm">
            {error?.message || 'Could not load the stock footage.'}
          </p>
        ) : services.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            This clinic has no active services.
          </p>
        ) : (
          <>
            {services.length > SEARCH_FROM ? (
              <div className="relative">
                <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={`Find one of ${services.length} services`}
                  aria-label="Find a service"
                  className="pl-8"
                />
              </div>
            ) : null}
            {shown.length === 0 ? (
              <p className="text-muted-foreground text-sm">
                No service matches "{search.trim()}".
              </p>
            ) : (
              <div className="divide-y">
                {shown.map((service) => (
                  <ServiceRow
                    key={service.id}
                    service={service}
                    clips={service.clipIds
                      .map((id) => clipsById.get(id))
                      .filter((c): c is StockClipSummary => !!c)}
                    onChoose={() => setChoosingId(service.id)}
                    onFixTags={() => setTaggingId(service.id)}
                    catalogue={stockFootage?.catalogue ?? []}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </CardContent>

      <ChooseServiceClipsDialog
        organizationId={organizationId}
        service={choosing}
        clipsById={clipsById}
        techniques={techniques}
        onOpenChange={(open) => {
          if (!open) setChoosingId(null);
        }}
      />
      <ServiceTagsDialog
        catalogue={stockFootage?.catalogue ?? []}
        organizationId={organizationId}
        service={tagging}
        techniques={techniques}
        onOpenChange={(open) => {
          if (!open) setTaggingId(null);
        }}
      />
    </Card>
  );
}

function ServiceRow({
  service,
  clips,
  onChoose,
  onFixTags,
  catalogue,
}: {
  service: ServiceStockClips;
  clips: StockClipSummary[];
  onChoose: () => void;
  onFixTags: () => void;
  /** Its treatment has several products or machines, so name the ones it uses. */
  catalogue: TreatmentCatalogueEntry[];
}) {
  return (
    <div className="flex flex-col gap-3 py-3 sm:flex-row sm:items-center">
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium text-sm">{service.name}</span>
          <Badge variant={service.mode === 'chosen' ? 'default' : 'secondary'}>
            {serviceStockClipModeLabels[service.mode]}
          </Badge>
        </div>
        <p className="text-muted-foreground text-xs">
          {readAs(service, catalogue)}{' '}
          <button
            type="button"
            className="font-medium text-foreground underline-offset-2 hover:underline"
            onClick={onFixTags}
          >
            Change
          </button>
        </p>
      </div>
      <Thumbnails clips={clips} />
      <Button
        variant="outline"
        size="sm"
        className="shrink-0"
        onClick={onChoose}
      >
        Choose clips
      </Button>
    </div>
  );
}

/**
 * What the service shows, which drives the automatic match: read by the AI
 * from its name, or set by hand.
 */
function readAs(
  service: ServiceStockClips,
  catalogue: TreatmentCatalogueEntry[]
): string {
  if (!service.treatments.length) {
    if (service.specSource === 'declared')
      return 'Other. No treatment stock is enabled.';
    return service.specSource === 'inferred_from_name' &&
      service.suggestions.length === 1
      ? 'Detected automatically from the service name.'
      : 'Other / not specified. No treatment stock is enabled.';
  }
  const choices = service.treatments
    .map((t) => {
      const name =
        catalogue.find((c) => c.slug === t.treatmentSlug)?.displayName ??
        'Unavailable treatment';
      const area =
        t.areaStatus === 'selected'
          ? t.regions.map((r) => bodyRegionLabels[r]).join(', ')
          : t.areaStatus === 'not_applicable'
            ? 'area not applicable'
            : 'area not specified';
      return `${name} (${area})`;
    })
    .join('; ');
  return `Confirmed: ${choices}.`;
}

function Thumbnails({ clips }: { clips: StockClipSummary[] }) {
  if (clips.length === 0) {
    return <span className="text-muted-foreground text-xs">No clips</span>;
  }
  const extra = clips.length - THUMBNAILS;
  return (
    <div className="flex shrink-0 items-center gap-1.5">
      {clips.slice(0, THUMBNAILS).map((clip) => (
        <Thumbnail key={clip.id} clip={clip} />
      ))}
      {extra > 0 ? (
        <span className="text-muted-foreground text-xs">+{extra}</span>
      ) : null}
    </div>
  );
}

/** A still from the part of the clip videos use. */
function Thumbnail({ clip }: { clip: StockClipSummary }) {
  const frame =
    'aspect-video w-16 rounded bg-black object-cover ring-1 ring-border';
  const title = clip.visualDescription ?? undefined;
  if (clip.mediaType === 'image') {
    return (
      <img
        src={clip.previewUrl}
        alt={title ?? ''}
        title={title}
        className={frame}
      />
    );
  }
  const at = clip.trimInMs != null ? clip.trimInMs / 1000 : 0.5;
  return (
    <video
      src={`${clip.previewUrl}#t=${at}`}
      muted
      playsInline
      preload="metadata"
      title={title}
      className={frame}
    />
  );
}
